import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  X,
  Check,
  ChevronUp
} from 'lucide-react';
import Header from './Header';
import Footer from './Footer';
import SectionNavigationDots from './SectionNavigationDots';

const CENTER_SECTION_CLASS = 'for-companies-section for-companies-snap-section flex items-center justify-center px-4';
const SPLIT_SECTION_CLASS = 'for-companies-section for-companies-snap-section for-companies-comparison-section grid lg:grid-cols-2';
const SECTION_NAV_MIN_HYSTERESIS = 32;
const SECTION_NAV_MAX_HYSTERESIS = 90;
const SECTION_NAV_HYSTERESIS_RATIO = 0.08;
const PROGRAMMATIC_SCROLL_LOCK_MS = 1400;
const PROGRAMMATIC_SCROLL_TARGET_TOLERANCE = 2;

const ForCompaniesPage = ({ isMenuOpen, setIsMenuOpen }) => {
  const navigate = useNavigate();
  const scrollContainerRef = useRef(null);
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);
  const [activeSection, setActiveSection] = useState(0);
  const activeSectionRef = useRef(0);
  const programmaticScrollRef = useRef({ index: null, targetTop: 0, timeoutId: null });
  const sectionRefs = useRef([]);

  // Section metadata
  const sections = [
    { id: 'hero', label: 'Start' },
    { id: 'equation', label: 'The Math' },
    { id: 'comparison', label: 'Comparison' },
    { id: 'team', label: 'Team' },
    { id: 'scale', label: 'Scale' },
    { id: 'zero-overhead', label: 'Zero Overhead' },
    { id: 'we-handle', label: 'We Handle Everything' },
    { id: 'how-it-works', label: 'Process' },
    { id: 'cta', label: 'Get Started' },
  ];

  const setActiveSectionIndex = useCallback((index) => {
    const nextIndex = Math.max(0, Math.min(index, sections.length - 1));
    activeSectionRef.current = nextIndex;
    setActiveSection(nextIndex);
  }, [sections.length]);

  const clearProgrammaticScroll = useCallback(() => {
    const { timeoutId } = programmaticScrollRef.current;

    if (timeoutId !== null) {
      window.clearTimeout(timeoutId);
    }

    programmaticScrollRef.current = { index: null, targetTop: 0, timeoutId: null };
  }, []);

  const startProgrammaticScroll = useCallback((index, targetTop) => {
    clearProgrammaticScroll();

    const timeoutId = window.setTimeout(() => {
      programmaticScrollRef.current = { index: null, targetTop: 0, timeoutId: null };
    }, PROGRAMMATIC_SCROLL_LOCK_MS);

    programmaticScrollRef.current = { index, targetTop, timeoutId };
  }, [clearProgrammaticScroll]);

  // Handle scroll to top
  const scrollToTop = () => {
    const container = scrollContainerRef.current;
    if (!container) return;

    setActiveSectionIndex(0);
    startProgrammaticScroll(0, 0);

    container.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  // Handle section navigation click
  const handleSectionClick = (index) => {
    const container = scrollContainerRef.current;
    const targetSection = sectionRefs.current[index];

    if (!container || !targetSection) return;

    const targetTop = Math.round(targetSection.offsetTop);

    setActiveSectionIndex(index);
    startProgrammaticScroll(index, targetTop);

    container.scrollTo({
      top: targetTop,
      behavior: 'smooth'
    });
  };

  // Monitor scroll position to show indicator after scrolling down
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop } = container;
      // Show after scrolling past 300px
      setShowScrollIndicator(scrollTop > 300);
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  // Track the active section with hysteresis so the nav dot does not jitter
  // while scroll snapping settles around the midpoint between two sections.
  useLayoutEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    let animationFrameId = null;

    const updateActiveSection = () => {
      animationFrameId = null;

      const sectionElements = sectionRefs.current.filter(Boolean);
      if (!sectionElements.length) return;

      const programmaticScroll = programmaticScrollRef.current;
      const { scrollTop } = container;

      if (programmaticScroll.index !== null) {
        if (Math.abs(scrollTop - programmaticScroll.targetTop) > PROGRAMMATIC_SCROLL_TARGET_TOLERANCE) {
          return;
        }

        clearProgrammaticScroll();
      }

      const hysteresis = Math.min(
        SECTION_NAV_MAX_HYSTERESIS,
        Math.max(
          SECTION_NAV_MIN_HYSTERESIS,
          container.clientHeight * SECTION_NAV_HYSTERESIS_RATIO
        )
      );

      const currentIndex = Math.min(activeSectionRef.current, sectionElements.length - 1);
      const currentSection = sectionElements[currentIndex];
      const currentDistance = currentSection
        ? Math.abs(scrollTop - currentSection.offsetTop)
        : Number.POSITIVE_INFINITY;

      let closestIndex = currentIndex;
      let closestDistance = currentDistance;

      sectionElements.forEach((section, index) => {
        const distance = Math.abs(scrollTop - section.offsetTop);

        if (distance < closestDistance) {
          closestIndex = index;
          closestDistance = distance;
        }
      });

      if (
        closestIndex !== currentIndex
        && closestDistance + hysteresis < currentDistance
      ) {
        setActiveSectionIndex(closestIndex);
      }
    };

    const scheduleActiveSectionUpdate = () => {
      if (animationFrameId !== null) return;
      animationFrameId = window.requestAnimationFrame(updateActiveSection);
    };

    const handleUserScrollIntent = () => {
      clearProgrammaticScroll();
      scheduleActiveSectionUpdate();
    };

    setActiveSectionIndex(activeSectionRef.current);
    scheduleActiveSectionUpdate();
    container.addEventListener('scroll', scheduleActiveSectionUpdate, { passive: true });
    container.addEventListener('wheel', handleUserScrollIntent, { passive: true });
    container.addEventListener('touchstart', handleUserScrollIntent, { passive: true });
    window.addEventListener('resize', scheduleActiveSectionUpdate);

    return () => {
      container.removeEventListener('scroll', scheduleActiveSectionUpdate);
      container.removeEventListener('wheel', handleUserScrollIntent);
      container.removeEventListener('touchstart', handleUserScrollIntent);
      window.removeEventListener('resize', scheduleActiveSectionUpdate);
      clearProgrammaticScroll();
      if (animationFrameId !== null) {
        window.cancelAnimationFrame(animationFrameId);
      }
    };
  }, [clearProgrammaticScroll, setActiveSectionIndex]);

  return (
    <div className="for-companies-page bg-slate-900 text-white">
      <Helmet>
        <title>For Companies | TukeWIN - IT Outsourcing</title>
        <meta name="description" content="IT outsourcing for companies. Get a dedicated team for the price of one employee. No HR, no overhead, no fixed costs." />
      </Helmet>

      {/* Fixed header - always visible */}
      <div className="fixed top-0 left-0 right-0 z-50">
        <Header isMenuOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen} />
      </div>

      {/* Scroll snap is enabled only by CSS on large, tall desktop viewports. */}
      <div
        ref={scrollContainerRef}
        className="for-companies-scroll-container scroll-smooth"
        id="scroll-container"
      >

        {/* ============================================ */}
        {/* HERO SECTION */}
        {/* ============================================ */}
        <section ref={(el) => (sectionRefs.current[0] = el)} className={`${CENTER_SECTION_CLASS} hero-bottom-fade section-glow-bottom overflow-hidden`}>
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(99,102,241,0.15),transparent_50%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,rgba(139,92,246,0.1),transparent_50%)]" />
        </div>

        <div className="relative z-10 text-center max-w-5xl mx-auto px-4 py-8">
          <h1 className="text-4xl sm:text-5xl md:text-7xl xl:text-8xl font-black mb-4 sm:mb-6 leading-[0.9] tracking-normal">
            <span className="block text-white">A TEAM</span>
            <span className="block bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent">
              FOR THE PRICE
            </span>
            <span className="block text-white">OF ONE</span>
          </h1>

          <p className="text-base sm:text-lg md:text-xl text-slate-400 mb-8 sm:mb-10 max-w-2xl mx-auto px-4">
            Stop hiring individuals. Partner with specialists who deliver.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-4">
            <button
              className="premium-btn flex items-center justify-center"
              onClick={() => handleSectionClick(8)}
            >
              Get Started <ArrowRight className="ml-2 h-5 w-5" />
            </button>
            <button
              className="secondary-btn"
              onClick={() => handleSectionClick(1)}
            >
              See The Math
            </button>
          </div>
        </div>

      </section>

        {/* ============================================ */}
        {/* THE EQUATION - Full Screen Math */}
        {/* ============================================ */}
        <section ref={(el) => (sectionRefs.current[1] = el)} id="equation" className={CENTER_SECTION_CLASS}>
        {/* Subtle background gradient with smooth transition */}
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900 via-slate-800/50 to-slate-900">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_0%,rgba(99,102,241,0.08),transparent_70%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_100%,rgba(139,92,246,0.08),transparent_70%)]" />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto text-center">
          {/* Employee side */}
          <div className="mb-8 sm:mb-12 xl:mb-16">
            <p className="text-slate-500 text-xs sm:text-sm uppercase tracking-[0.3em] mb-3 sm:mb-4">Traditional Approach</p>
            <h2 className="text-3xl sm:text-5xl lg:text-6xl xl:text-7xl font-black text-white mb-3 sm:mb-4">
              1 EMPLOYEE
            </h2>
            <div className="w-24 sm:w-32 h-1 bg-gradient-to-r from-red-500/50 to-orange-500/50 mx-auto mb-4 sm:mb-6" />
            <p className="text-xl sm:text-2xl md:text-3xl text-slate-400 font-light">
              €50,000<span className="text-slate-600">+</span> / year
            </p>
            <p className="text-slate-500 mt-3 sm:mt-4 max-w-md mx-auto text-sm sm:text-base px-4">
              Plus recruitment fees, payroll taxes, benefits, office space, equipment, training...
            </p>
          </div>

          {/* VS divider */}
          <div className="relative py-4 sm:py-8">
            <div className="absolute left-1/2 -translate-x-1/2 w-px h-full bg-gradient-to-b from-transparent via-slate-600 to-transparent" />
            <span className="relative z-10 inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-slate-800 border border-slate-700 text-slate-400 font-bold text-base sm:text-lg">
              VS
            </span>
          </div>

          {/* TukeWIN side */}
          <div className="mt-8 sm:mt-12 xl:mt-16">
            <p className="text-indigo-400 text-xs sm:text-sm uppercase tracking-[0.3em] mb-3 sm:mb-4">Our Approach</p>
            <h2 className="text-3xl sm:text-5xl lg:text-6xl xl:text-7xl font-black mb-3 sm:mb-4">
              <span className="bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent">
                FULL TEAM
              </span>
            </h2>
            <div className="w-24 sm:w-32 h-1 bg-gradient-to-r from-indigo-500 to-purple-500 mx-auto mb-4 sm:mb-6" />
            <p className="text-xl sm:text-2xl md:text-3xl text-slate-300 font-light">
              Same budget. Zero overhead.
            </p>
            <p className="text-slate-400 mt-3 sm:mt-4 max-w-md mx-auto text-sm sm:text-base px-4">
              We handle everything. You just get results.
            </p>

            {/* Benefits row */}
            <div className="flex flex-wrap justify-center gap-2 sm:gap-4 mt-6 sm:mt-10 px-2">
              {['Zero HR', 'Zero Hassle', 'Zero Office', 'Zero Risk'].map((item, i) => (
                <div key={i} className="flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-indigo-500/10 border border-indigo-500/20">
                  <div className="w-1.5 h-1.5 rounded-full bg-indigo-400" />
                  <span className="text-indigo-300 text-xs sm:text-sm font-medium">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

        {/* ============================================ */}
        {/* SPLIT SCREEN - Problem vs Solution */}
        {/* ============================================ */}
        <section ref={(el) => (sectionRefs.current[2] = el)} className={SPLIT_SECTION_CLASS}>
        {/* Smooth gradient overlay for entire section */}
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 pointer-events-none" />

        {/* Left - Traditional (Problem) */}
        <div className="relative flex flex-col justify-center items-center lg:items-end p-6 sm:p-8 lg:p-16 lg:pr-20 xl:pr-28 py-12 lg:py-0">
          {/* Red tint overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-red-950/20 via-transparent to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/50 to-transparent" />

          <div className="relative z-10 max-w-md">
            <div className="flex items-center gap-3 mb-4 sm:mb-8">
              <X className="h-5 w-5 sm:h-6 sm:w-6 text-red-400" />
              <span className="text-red-400 text-xs sm:text-sm uppercase tracking-[0.2em] font-medium">Traditional Hiring</span>
            </div>

            <h2 className="text-2xl sm:text-3xl lg:text-5xl font-black text-white mb-6 sm:mb-10 leading-tight">
              THE OLD<br />
              <span className="text-slate-500">WAY</span>
            </h2>

            <div className="space-y-3 sm:space-y-6">
              {[
                'Months of recruitment',
                'Payroll taxes & benefits',
                'Office space & equipment',
                'Training & onboarding',
                'Risk of bad hires'
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3 sm:gap-4 group">
                  <div className="w-2 h-2 rounded-full bg-red-500/50 group-hover:bg-red-400 transition-colors flex-shrink-0" />
                  <span className="text-slate-400 text-sm sm:text-lg group-hover:text-slate-300 transition-colors">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right - TukeWIN (Solution) */}
        <div className="relative flex flex-col justify-center items-center lg:items-start p-6 sm:p-8 lg:p-16 lg:pl-20 xl:pl-28 py-12 lg:py-0">
          {/* Green tint overlay */}
          <div className="absolute inset-0 bg-gradient-to-bl from-emerald-950/20 via-transparent to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/50 to-transparent" />

          <div className="relative z-10 max-w-md">
            <div className="flex items-center gap-3 mb-4 sm:mb-8">
              <Check className="h-5 w-5 sm:h-6 sm:w-6 text-emerald-400" />
              <span className="text-emerald-400 text-xs sm:text-sm uppercase tracking-[0.2em] font-medium">With TukeWIN</span>
            </div>

            <h2 className="text-2xl sm:text-3xl lg:text-5xl font-black text-white mb-6 sm:mb-10 leading-tight">
              THE SMART<br />
              <span className="bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent">WAY</span>
            </h2>

            <div className="space-y-3 sm:space-y-6">
              {[
                'Instant team capacity',
                'One simple invoice',
                'Our infrastructure & equipment',
                'Pre-trained experts',
                'We deliver or fix it'
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3 sm:gap-4 group">
                  <Check className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-400 group-hover:scale-110 transition-transform flex-shrink-0" />
                  <span className="text-slate-300 text-sm sm:text-lg group-hover:text-white transition-colors">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

        {/* ============================================ */}
        {/* BENEFIT 1: TEAM */}
        {/* ============================================ */}
        <section ref={(el) => (sectionRefs.current[3] = el)} className={`${CENTER_SECTION_CLASS} overflow-hidden`}>
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900 via-slate-800/30 to-slate-900" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_0%,rgba(99,102,241,0.06),transparent_70%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_100%,rgba(139,92,246,0.06),transparent_70%)]" />

        {/* Team network visualization - hidden on mobile */}
        <div className="absolute left-1/2 top-[54%] -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] opacity-15 hidden xl:block">
          {/* 5 nodes matching the 5 team members, positioned in a pentagon */}
          {/* Designer - pink/rose - top */}
          <div className="absolute left-1/2 top-[5%] -translate-x-1/2 w-10 h-10 rounded-full bg-gradient-to-br from-pink-500/25 to-rose-500/25 border border-pink-400/15" />
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 w-px h-[45%] origin-top rotate-180 bg-gradient-to-b from-indigo-500/30 to-pink-400/15" />

          {/* Frontend - blue/cyan - top right */}
          <div className="absolute left-[88%] top-[35%] -translate-x-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-gradient-to-br from-blue-500/25 to-cyan-500/25 border border-blue-400/15" />
          <div className="absolute left-1/2 top-1/2 w-[40%] h-px origin-left -rotate-[20deg] bg-gradient-to-r from-indigo-500/30 to-blue-400/15" />

          {/* Backend - green/emerald - bottom right */}
          <div className="absolute left-[75%] top-[85%] -translate-x-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-gradient-to-br from-green-500/25 to-emerald-500/25 border border-green-400/15" />
          <div className="absolute left-1/2 top-1/2 w-[35%] h-px origin-left rotate-[55deg] bg-gradient-to-r from-indigo-500/30 to-green-400/15" />

          {/* UX - purple/violet - bottom left */}
          <div className="absolute left-[25%] top-[85%] -translate-x-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-gradient-to-br from-purple-500/25 to-violet-500/25 border border-purple-400/15" />
          <div className="absolute left-1/2 top-1/2 w-[35%] h-px origin-left rotate-[125deg] bg-gradient-to-r from-indigo-500/30 to-purple-400/15" />

          {/* Project Mgr - orange/amber - top left */}
          <div className="absolute left-[12%] top-[35%] -translate-x-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-gradient-to-br from-orange-500/25 to-amber-500/25 border border-orange-400/15" />
          <div className="absolute left-1/2 top-1/2 w-[40%] h-px origin-left rotate-[200deg] bg-gradient-to-r from-indigo-500/30 to-orange-400/15" />

          {/* Center hub - rendered last to be on top of lines, solid bg to cover lines */}
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-14 h-14 rounded-full bg-slate-800 border border-indigo-400/20">
            <div className="absolute inset-0 rounded-full bg-gradient-to-br from-indigo-500/40 to-purple-500/40" />
          </div>
        </div>

        <div className="relative z-10 text-center max-w-4xl mx-auto">
          <p className="text-indigo-400 text-xs sm:text-sm uppercase tracking-[0.3em] mb-4 sm:mb-6">What You Get</p>

          <h2 className="text-3xl sm:text-5xl md:text-7xl xl:text-8xl font-black leading-[0.9] mb-4 sm:mb-8">
            <span className="text-slate-500">YOU GET A</span><br />
            <span className="bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent">TEAM</span>
          </h2>

          <p className="text-base sm:text-xl md:text-2xl text-slate-400 mb-8 sm:mb-12 xl:mb-16 max-w-2xl mx-auto px-4">
            Not a single hire. An entire squad of specialists collaborating on your project.
          </p>

          {/* Team visualization */}
          <div className="flex flex-wrap justify-center gap-4 sm:gap-6 md:gap-10 px-2">
            {[
              { letter: 'D', role: 'Designer', color: 'from-pink-500 to-rose-500', ring: 'ring-pink-500/20', glow: 'shadow-pink-500/25' },
              { letter: 'F', role: 'Frontend', color: 'from-blue-500 to-cyan-500', ring: 'ring-blue-500/20', glow: 'shadow-blue-500/25' },
              { letter: 'B', role: 'Backend', color: 'from-green-500 to-emerald-500', ring: 'ring-green-500/20', glow: 'shadow-green-500/25' },
              { letter: 'U', role: 'UX', color: 'from-purple-500 to-violet-500', ring: 'ring-purple-500/20', glow: 'shadow-purple-500/25' },
              { letter: 'PM', role: 'Project Mgr', color: 'from-orange-500 to-amber-500', ring: 'ring-orange-500/20', glow: 'shadow-orange-500/25' },
            ].map((member, i) => (
              <div key={i} className="flex flex-col items-center group">
                <div className="relative w-12 h-12 sm:w-16 sm:h-16 lg:w-20 lg:h-20 xl:w-24 xl:h-24">
                  {/* Outer ring */}
                  <div className={`absolute inset-0 rounded-full ring-2 ${member.ring} group-hover:ring-4 transition-all duration-300`} />
                  {/* Main circle */}
                  <div className={`absolute inset-1 rounded-full bg-gradient-to-br ${member.color}
                                  flex items-center justify-center text-white font-bold text-sm sm:text-lg md:text-2xl
                                  shadow-lg ${member.glow} group-hover:shadow-xl group-hover:scale-105 transition-all duration-300`}>
                    {member.letter}
                  </div>
                </div>
                <span className="mt-2 sm:mt-4 text-slate-400 text-xs sm:text-sm font-medium tracking-wide group-hover:text-slate-300 transition-colors">{member.role}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

        {/* ============================================ */}
        {/* BENEFIT 2: SCALE */}
        {/* ============================================ */}
        <section ref={(el) => (sectionRefs.current[4] = el)} className={`section-bg-light-glow ${CENTER_SECTION_CLASS}`}>
        <div className="text-center max-w-4xl mx-auto">
          <p className="text-indigo-400 text-xs sm:text-sm uppercase tracking-[0.3em] mb-4 sm:mb-6">Flexibility</p>

          <h2 className="text-3xl sm:text-5xl md:text-7xl xl:text-8xl font-black leading-[0.9] mb-2 sm:mb-4">
            <span className="bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent">SCALE</span>
          </h2>
          <h3 className="text-2xl sm:text-4xl md:text-5xl xl:text-6xl font-black text-slate-500 mb-4 sm:mb-8">
            UP OR DOWN
          </h3>

          <p className="text-base sm:text-lg md:text-xl text-slate-400 mb-8 sm:mb-12 xl:mb-16 max-w-xl mx-auto px-4">
            whenever you need
          </p>

          {/* Capacity visualization */}
          <div className="flex justify-center items-end gap-1.5 sm:gap-2 md:gap-3 mb-8 sm:mb-12 h-20 sm:h-28 md:h-32">
            {[20, 40, 60, 80, 100, 80, 60, 40, 60, 80].map((height, i) => (
              <div
                key={i}
                className="w-4 sm:w-6 md:w-8 rounded-t-lg bg-gradient-to-t from-indigo-600 to-purple-500 transition-all duration-300 hover:from-indigo-500 hover:to-purple-400"
                style={{ height: `${height}%` }}
              />
            ))}
          </div>

          <p className="text-lg sm:text-2xl md:text-3xl text-slate-300 font-light px-4">
            No contracts. No severance. No drama.
          </p>
        </div>
      </section>

        {/* ============================================ */}
        {/* BENEFIT 3: ZERO OVERHEAD */}
        {/* ============================================ */}
        <section ref={(el) => (sectionRefs.current[5] = el)} className={CENTER_SECTION_CLASS}>
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900 via-slate-800/30 to-slate-900" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_0%,rgba(99,102,241,0.06),transparent_70%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_100%,rgba(139,92,246,0.06),transparent_70%)]" />

        <div className="relative z-10 text-center max-w-3xl mx-auto">
          <p className="text-indigo-400 text-xs sm:text-sm uppercase tracking-[0.3em] mb-6 sm:mb-10 xl:mb-12">Your Savings</p>

          <div className="space-y-6 sm:space-y-10 xl:space-y-16">
            {[
              { label: 'RECRUITMENT FEES' },
              { label: 'PAYROLL TAXES' },
              { label: 'OFFICE COSTS' },
            ].map((item, i) => (
              <div key={i}>
                <div className="text-5xl sm:text-7xl md:text-8xl xl:text-9xl font-black bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent">
                  €0
                </div>
                <div className="w-16 sm:w-24 h-0.5 bg-slate-700 mx-auto my-2 sm:my-4" />
                <p className="text-slate-500 text-xs sm:text-sm md:text-base uppercase tracking-[0.2em]">{item.label}</p>
              </div>
            ))}
          </div>

          <p className="mt-8 sm:mt-12 xl:mt-16 text-xl sm:text-2xl md:text-3xl text-slate-300 font-light">
            Just results.
          </p>
        </div>
      </section>

        {/* ============================================ */}
        {/* BENEFIT 4: WE HANDLE EVERYTHING */}
        {/* ============================================ */}
        <section ref={(el) => (sectionRefs.current[6] = el)} className={`section-bg-light-glow ${CENTER_SECTION_CLASS} overflow-hidden`}>
        <div className="max-w-5xl mx-auto w-full">
          <div className="grid xl:grid-cols-2 gap-8 sm:gap-12 xl:gap-20 items-center">
            {/* Your Part */}
            <div className="text-center xl:text-left">
              <p className="text-slate-500 text-xs sm:text-sm uppercase tracking-[0.3em] mb-4 sm:mb-6">Your Part</p>
              <h2 className="text-2xl sm:text-3xl md:text-4xl xl:text-5xl font-black text-white mb-4 sm:mb-8 leading-tight">
                Tell us what you need.
              </h2>
              <p className="text-base sm:text-xl text-slate-400">
                Review our work.
              </p>
              <p className="text-slate-600 text-base sm:text-lg mt-2 sm:mt-4">
                That's it.
              </p>
            </div>

            {/* Divider */}
            <div className="hidden xl:block absolute left-1/2 top-1/4 bottom-1/4 w-px bg-gradient-to-b from-transparent via-slate-600 to-transparent" />

            {/* Our Part */}
            <div className="text-center xl:text-right">
              <p className="text-indigo-400 text-xs sm:text-sm uppercase tracking-[0.3em] mb-4 sm:mb-6">Our Part</p>
              <h2 className="text-2xl sm:text-3xl md:text-4xl xl:text-5xl font-black mb-4 sm:mb-8 leading-tight">
                <span className="bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent">
                  Everything else.
                </span>
              </h2>

              {/* Floating task labels */}
              <div className="flex flex-wrap justify-center xl:justify-end gap-2 sm:gap-3 mt-4 sm:mt-8">
                {['Planning', 'Execution', 'QA', 'Delivery', 'Management', 'Communication'].map((task, i) => (
                  <span
                    key={i}
                    className="px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-slate-800/80 border border-slate-700/50
                               text-slate-400 text-xs sm:text-sm hover:border-indigo-500/30 hover:text-slate-300 transition-all"
                  >
                    {task}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

        {/* ============================================ */}
        {/* PROCESS - Minimal Flow */}
        {/* ============================================ */}
        <section ref={(el) => (sectionRefs.current[7] = el)} id="how-it-works" className={CENTER_SECTION_CLASS}>
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900 via-slate-800/30 to-slate-900" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_0%,rgba(99,102,241,0.06),transparent_70%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_100%,rgba(139,92,246,0.06),transparent_70%)]" />

        <div className="relative z-10 max-w-4xl mx-auto w-full">
          <div className="text-center mb-10 sm:mb-14 xl:mb-20">
            <p className="text-indigo-400 text-xs sm:text-sm uppercase tracking-[0.3em] mb-3 sm:mb-4">Process</p>
            <h2 className="text-2xl sm:text-3xl md:text-5xl font-black">
              <span className="bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent">
                How It Works
              </span>
            </h2>
          </div>

          <div className="space-y-6 sm:space-y-10 xl:space-y-16">
            {[
              { num: '01', title: 'CONTACT', desc: 'Tell us your needs' },
              { num: '02', title: 'TEAM', desc: 'We assemble specialists' },
              { num: '03', title: 'DELIVERY', desc: 'We work, you review' },
              { num: '04', title: 'SCALE', desc: 'Adjust capacity anytime' },
            ].map((step, i) => (
              <div key={i} className="group">
                <div className="flex items-center gap-3 sm:gap-6 md:gap-8">
                  <span className="text-2xl sm:text-3xl md:text-4xl font-black text-indigo-500/30 group-hover:text-indigo-500/50 transition-colors">
                    {step.num}
                  </span>
                  <div className="flex-1 h-px bg-gradient-to-r from-slate-700 to-transparent" />
                  <span className="text-base sm:text-lg md:text-xl font-bold text-white group-hover:text-indigo-400 transition-colors">
                    {step.title}
                  </span>
                </div>
                <p className="text-slate-500 text-sm sm:text-base mt-1 sm:mt-2 pl-12 sm:pl-16 md:pl-20">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

        {/* ============================================ */}
        {/* CTA */}
        {/* ============================================ */}
        <section ref={(el) => (sectionRefs.current[8] = el)} id="cta" className={CENTER_SECTION_CLASS}>
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900 via-slate-800/30 to-slate-900" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_0%,rgba(99,102,241,0.06),transparent_70%)]" />

        <div className="relative z-10 max-w-4xl mx-auto w-full">
          <div className="relative overflow-hidden rounded-2xl sm:rounded-3xl">
            <div className="absolute inset-0 bg-gradient-to-br from-indigo-600/20 via-purple-600/20 to-indigo-600/20" />
            <div className="absolute inset-0 bg-slate-900/80" />
            <div className="absolute inset-0 opacity-30"
                 style={{
                   backgroundImage: `radial-gradient(circle at 20% 50%, rgba(99,102,241,0.3) 0%, transparent 50%),
                                     radial-gradient(circle at 80% 50%, rgba(139,92,246,0.3) 0%, transparent 50%)`
                 }} />

            <div className="relative z-10 p-6 sm:p-10 md:p-16 text-center">
              <h2 className="text-2xl sm:text-3xl md:text-5xl font-black mb-4 sm:mb-6">
                <span className="bg-gradient-to-r from-white to-slate-300 bg-clip-text text-transparent">
                  Ready to Scale?
                </span>
              </h2>
              <p className="text-base sm:text-lg md:text-xl text-slate-300 mb-6 sm:mb-8 max-w-2xl mx-auto px-2">
                Let's discuss your project. No pressure, no commitment.
              </p>
              <button
                className="premium-btn flex items-center justify-center mx-auto"
                onClick={() => navigate('/contact')}
              >
                Contact Us <ArrowRight className="ml-2 h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </section>

        {/* ============================================ */}
        {/* FOOTER */}
        {/* ============================================ */}
        <div>
          <Footer />
        </div>

      </div>
      {/* Scroll snap container closing */}

      {/* Section navigation dots */}
      <SectionNavigationDots
        sections={sections}
        activeSection={activeSection}
        onSectionClick={handleSectionClick}
        className="for-companies-section-nav"
      />

      {/* Scroll to top indicator - mobile only */}
      <button
        onClick={scrollToTop}
        className={`
          fixed bottom-6 right-6 z-40
          md:hidden
          w-11 h-11 rounded-full
          bg-slate-800/60 backdrop-blur-sm
          border border-slate-700/50
          text-slate-400
          shadow-lg
          transition-all duration-300 ease-in-out
          hover:bg-slate-700/70 hover:border-slate-600/60
          hover:text-slate-300 hover:scale-105
          focus:outline-none focus:ring-2 focus:ring-indigo-500/50
          flex items-center justify-center
          group
          ${showScrollIndicator
            ? 'opacity-70 hover:opacity-100 translate-y-0 pointer-events-auto'
            : 'opacity-0 translate-y-4 pointer-events-none'
          }
        `}
        aria-label="Scroll to top"
        title="Scroll to top"
      >
        <ChevronUp
          className="w-5 h-5 transition-transform duration-200 group-hover:-translate-y-0.5"
          strokeWidth={2}
        />
      </button>

    </div>
  );
};

export default ForCompaniesPage;
